import {
  getTargetActivityOptions,
  getTargetActivityById
} from "../../data/planning/targetActivityCatalog";

import {
  getMinimumLevel1ForAudience
} from "../../data/planning/planningRules";

import {
  getAutoTargetActivityRule
} from "../../data/planning/autoTargetActivityRules";



/**
 * REHBER360 - OTOMATİK HEDEF FAALİYETİ ÇÖZÜMLEYİCİ
 *
 * V1 görevi:
 *
 * Seçilen hedef ile resmî faaliyet havuzunda
 * aynı ada sahip güvenli bir faaliyet varsa
 * bunu bulmak.
 *
 * Bu dosya:
 * - faaliyet adı üretmez,
 * - fuzzy / AI tahmini yapmaz,
 * - ilk bulduğu rastgele faaliyeti seçmez,
 * - doğrulanmamış hedef → faaliyet ilişkisi kurmaz.
 */


const normalizeOfficialLabel =
  value => {

    return String(
      value || ""
    )
      .trim()
      .toLocaleLowerCase(
        "tr-TR"
      )
      .replace(
        /\s+/g,
        " "
      );
  };




// =========================================================
// AÇIK KURAL EŞLEMESİ
// =========================================================

const resolveExplicitTargetActivities = ({
  target,
  audience,
  schoolLevel = null
} = {}) => {

  if (
    !target?.id ||
    !audience
  ) {

    return {
      success: false,
      activities: [],
      reason:
        "TARGET_OR_AUDIENCE_MISSING"
    };
  }


 const rule =
  getAutoTargetActivityRule(
    target.id,
    audience,
    schoolLevel
  );


const activityIds =
  Array.isArray(
    rule?.activityIds
  )
    ? rule.activityIds
    : [];


  if (
    !Array.isArray(activityIds) ||
    activityIds.length === 0
  ) {

    return {
      success: false,
      activities: [],
      reason:
        "EXPLICIT_MAPPING_NOT_FOUND"
    };
  }


  const activities =
    activityIds
      .map(activityId =>
        getTargetActivityById(
          activityId
        )
      );


  /*
   * Mapping tablosundaki tek bir ID bile
   * geçersiz veya yanlış hedef kitleye aitse
   * mapping bütünüyle reddedilir.
   */
  const hasInvalidActivity =
    activities.some(
      activity =>
        !activity ||
        activity.audience !== audience
    );


  if (hasInvalidActivity) {

    return {
      success: false,
      activities: [],
      reason:
        "EXPLICIT_MAPPING_INVALID"
    };
  }


  /*
   * Öğrenci faaliyetlerinde gelişim alanı
   * hedef ile uyumlu olmak zorundadır.
   */
  const hasDevelopmentAreaMismatch =
    audience === "student" &&
    target.developmentArea &&
    activities.some(
      activity =>
        activity.developmentArea !==
          target.developmentArea
    );


  if (hasDevelopmentAreaMismatch) {

    return {
      success: false,
      activities: [],
      reason:
        "EXPLICIT_MAPPING_DEVELOPMENT_AREA_MISMATCH"
    };
  }


  return {

    success: true,

    activities,

    reason:
      "EXPLICIT_VERIFIED_MAPPING"

  };
};



// =========================================================
// BİREBİR RESMÎ AD EŞLEŞMESİ
// =========================================================

export const resolveExactTargetActivity = ({
  target,
  audience
} = {}) => {

  if (
    !target?.label ||
    !audience
  ) {

    return {
      success: false,
      activity: null,
      reason:
        "TARGET_OR_AUDIENCE_MISSING"
    };
  }


  const options =
    getTargetActivityOptions({

      audience,

      developmentArea:
        audience === "student"
          ? target.developmentArea ||
            null
          : null

    });


  const normalizedTargetLabel =
    normalizeOfficialLabel(
      target.label
    );


  const exactMatches =
    options.filter(
      activity =>
        normalizeOfficialLabel(
          activity.officialLabel
        ) ===
        normalizedTargetLabel
    );


  if (
    exactMatches.length !== 1
  ) {

    return {

      success: false,

      activity: null,

      reason:
        exactMatches.length > 1
          ? "MULTIPLE_EXACT_MATCHES"
          : "EXACT_MATCH_NOT_FOUND"

    };
  }


  return {

    success: true,

    activity:
      exactMatches[0],

    reason:
      "EXACT_OFFICIAL_LABEL_MATCH"

  };
};

// =========================================================
// HEDEF İÇİN TÜM GÜVENLİ FAALİYETLERİ ÇÖZ
// =========================================================

const resolveTargetActivities = ({
  target,
  audience,
  schoolLevel = null
} = {}) => {

  const exactResult =
    resolveExactTargetActivity({
      target,
      audience
    });


 const explicitResult =
  resolveExplicitTargetActivities({
    target,
    audience,
    schoolLevel
  });


  const candidates = [

    ...(
      exactResult.success &&
      exactResult.activity
        ? [exactResult.activity]
        : []
    ),

    ...(
      explicitResult.success
        ? explicitResult.activities
        : []
    )

  ];


  /*
   * Aynı faaliyet hem birebir eşleşmeden
   * hem explicit mapping'den gelirse
   * yalnızca bir kez tutulur.
   */
  const activities =
    candidates.filter(
      (activity, index, array) =>
        array.findIndex(
          candidate =>
            candidate.id ===
              activity.id
        ) === index
    );


 if (activities.length === 0) {

  return {

    success: false,

    activities: [],

    reason:
      exactResult.reason ===
        "MULTIPLE_EXACT_MATCHES"
        ? exactResult.reason
        : explicitResult.reason,

    explicitMappingSuccess:
      explicitResult.success,

    explicitMappingReason:
      explicitResult.reason

  };
}


  let reason =
    "EXACT_OFFICIAL_LABEL_MATCH";


  if (
    exactResult.success &&
    explicitResult.success
  ) {

    reason =
      "EXACT_AND_EXPLICIT_VERIFIED_MAPPING";

  } else if (
    explicitResult.success
  ) {

    reason =
      "EXPLICIT_VERIFIED_MAPPING";
  }


  return {

  success: true,

  activities,

  reason,

  explicitMappingSuccess:
    explicitResult.success,

  explicitMappingReason:
    explicitResult.reason

};
};

export const resolveAutoTargetActivities = ({
  selectedTargets = [],
  planningProfileId = "regular_school",
  schoolLevel = null
} = {}) => {

  const audiences = [
    "student",
    "parent",
    "teacher"
  ];


  const resolutions = [];

  const unresolved = [];


  (
    Array.isArray(
      selectedTargets
    )
      ? selectedTargets
      : []
  ).forEach(target => {

    if (!target?.id) {
      return;
    }


    audiences.forEach(
      audience => {

               const result =
  resolveTargetActivities({
    target,
    audience,
    schoolLevel
  });


        const resolution = {

          targetType:
            target.type,

          targetId:
            target.id,

          targetLabel:
            target.label,

          developmentArea:
            target.developmentArea ||
            null,

          audience,

          resolved:
            result.success,

         resolutionReason:
  result.reason,

explicitMappingSuccess:
  result.explicitMappingSuccess ===
    true,

explicitMappingReason:
  result.explicitMappingReason ||
  null,

activities:
  result.activities ||
  [],

          activityIds:
            (
              result.activities ||
              []
            ).map(
              activity =>
                activity.id
            ),

          officialLabels:
            (
              result.activities ||
              []
            ).map(
              activity =>
                activity.officialLabel
            ),

          resolvedActivityCount:
            (
              result.activities ||
              []
            ).length,

          /*
           * Mevcut teşhis ekranıyla
           * geriye uyumluluk.
           */
          activityId:
            result.activities?.[0]
              ?.id ||
            null,

          officialLabel:
            result.activities?.[0]
              ?.officialLabel ||
            null

        };


        resolutions.push(
          resolution
        );


        if (!result.success) {

          unresolved.push(
            resolution
          );
        }

      }
    );

  });


  // =========================================================
  // HEDEF / KİTLE BAZLI MİNİMUM ADAY KAPSAMI
  // =========================================================
  //
  // ÖNEMLİ:
  // Burada henüz faaliyetlere otomatik Düzey 1 atanmadı.
  // Bu nedenle bu kontrol:
  //
  // "Düzey 1 minimumunu karşılayabilecek kadar
  // doğrulanmış faaliyet adayı var mı?"
  //
  // sorusunu cevaplar.
  // Gerçek Düzey 1 uygunluğu faaliyet türü / düzey
  // seçim katmanında ayrıca doğrulanacaktır.
  // =========================================================

  const minimumCoverage =
    resolutions.map(
      resolution => {

        const rule =
          getMinimumLevel1ForAudience(
            planningProfileId,
            resolution.audience
          ) || {
            count: 0,
            enforcement:
              "not_required"
          };


        const requiredLevel1Count =
          Number(
            rule.count || 0
          );


        const resolvedCandidateCount =
          Number(
            resolution
              .resolvedActivityCount ||
            0
          );


        const missingCandidateCount =
          Math.max(
            0,
            requiredLevel1Count -
              resolvedCandidateCount
          );


        return {

          targetType:
            resolution.targetType,

          targetId:
            resolution.targetId,

          targetLabel:
            resolution.targetLabel,

          audience:
            resolution.audience,

          enforcement:
            rule.enforcement ||
            "not_required",

          requiredLevel1Count,

          resolvedCandidateCount,

          missingCandidateCount,

          candidateCountSufficient:
            resolvedCandidateCount >=
            requiredLevel1Count

        };

      }
    );


  const coverageDeficits =
    minimumCoverage.filter(
      item =>
        !item.candidateCountSufficient
    );


  const requiredCoverageDeficits =
    coverageDeficits.filter(
      item =>
        item.enforcement ===
          "required"
    );


  const requiredCoverageComplete =
    requiredCoverageDeficits.length ===
    0;


  return {

    success:
      unresolved.length === 0,

    resolutions,

    unresolved,

    resolvedCount:
      resolutions.length -
      unresolved.length,

        unresolvedCount:
      unresolved.length,

    minimumCoverage,

    coverageDeficits,

    requiredCoverageDeficits,

    requiredCoverageComplete

  };
};